import React, { useState } from 'react';
import GoogleLogin from '../GoogleLogin';

// /signout: the "Sign out" link on the docs page (hermespm.ai redirects it here).
// Signing out of the website also revokes every agent grant the user approved
// (POST /auth/logout cascade, see API.md), so the page asks first: a plain link
// must never sign anyone out. Rendered by App.js for both signed-in and signed-out
// visitors, at the same position, so the "done" state survives the logout.

export default function SignOut({ user, onLogout, onLogin }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  const cancel = () => {
    if (window.history.length > 1) window.history.back();
    else window.location.href = '/';
  };

  const signOut = async () => {
    setBusy(true);
    await onLogout();
    setDone(true);
    setBusy(false);
  };

  if (done) {
    return (
      <Shell>
        <h1 style={S.h1}>You're signed out</h1>
        <p style={S.p}>Your agents are disconnected too. They'll ask you to sign in again next time you use Hermes PM.</p>
        <p style={S.muted}>You can close this page.</p>
      </Shell>
    );
  }

  if (!user) {
    if (showLogin) return <GoogleLogin onSuccess={onLogin} />;
    return (
      <Shell>
        <h1 style={S.h1}>You're not signed in here</h1>
        <p style={S.p}>
          This browser isn't signed in to Hermes PM. An agent you allowed may still be connected: to
          disconnect all of them, sign in and then sign out. To disconnect just one, ask that agent
          to reset Hermes PM.
        </p>
        <div style={S.row}>
          <button style={S.primary} onClick={() => setShowLogin(true)}>Sign in</button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 style={S.h1}>Sign out of Hermes PM?</h1>
      {user.email && <p style={S.p}>Signed in as <strong>{user.email}</strong>.</p>}
      <p style={S.p}>
        This signs you out in this browser and disconnects every agent you allowed (Hermes, Claude
        Code, Codex). They'll ask you to sign in again next time.
      </p>
      <div style={S.row}>
        <button style={S.secondary} onClick={cancel} disabled={busy}>Cancel</button>
        <button style={S.primary} onClick={signOut} disabled={busy}>
          {busy ? 'Signing out…' : 'Sign out'}
        </button>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.brand}>Hermes PM</div>
        {children}
      </div>
    </div>
  );
}

const S = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ffffff', padding: 16, boxSizing: 'border-box' },
  card: { width: '100%', maxWidth: 460, background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '28px 24px', color: '#111827', boxSizing: 'border-box' },
  brand: { fontSize: 13, letterSpacing: '.04em', color: '#6b7280', marginBottom: 14, textTransform: 'uppercase' },
  h1: { fontSize: 22, margin: '0 0 12px', fontWeight: 600, lineHeight: 1.3 },
  p: { fontSize: 15, lineHeight: 1.55, margin: '0 0 12px', color: '#1f2937' },
  muted: { fontSize: 13, lineHeight: 1.55, color: '#6b7280', margin: '16px 0 0' },
  row: { display: 'flex', gap: 12, marginTop: 8 },
  primary: { flex: 1, padding: '12px 16px', fontSize: 15, fontWeight: 600, background: '#2457d6', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer' },
  secondary: { flex: 1, padding: '12px 16px', fontSize: 15, fontWeight: 600, background: '#ffffff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer' },
};
